import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { Navigate } from 'react-router-dom';
import { formatEther } from 'viem';
import DAppHeader from '../../components/DAppHeader/DAppHeader';
import VideoGroupCard from '../../components/VideoGroupCard/VideoGroupCard';
import { useUserBetsGrouped } from '../../hooks/useContracts';
import './MyBetsPage.scss';

export default function MyBetsPage() {
  const { isConnected } = useAccount();
  const { groupedBets, isLoading } = useUserBetsGrouped();


  const [filter, setFilter] = useState<'active' | 'ready_to_claim' | 'all' | 'finished'>('active');
  const [hasInitializedFilter, setHasInitializedFilter] = useState(false);

  // Helper to check if a bet is winning
  const isBetWinning = (bet: any, video: any) => {
    if (!video.isViewsSet) return false;
    const views = video.views;
    const { threshold1, threshold2, threshold3 } = video.thresholds;
    const index = Number(bet.thresholdIndex);

    if (index === 0) return views <= threshold1;
    if (index === 1) return views > threshold1 && views <= threshold2;
    if (index === 2) return views > threshold2 && views <= threshold3;
    if (index === 3) return views > threshold3;
    return false;
  };

  // Initialize filter based on claimable bets
  useEffect(() => {
    if (!isLoading && groupedBets.length > 0 && !hasInitializedFilter) {
      const hasClaimableBets = groupedBets.some(group =>
        group.video.isViewsSet &&
        group.bets.some(bet => isBetWinning(bet, group.video) && !bet.claimed)
      );

      if (hasClaimableBets) {
        setFilter('ready_to_claim');
      }
      setHasInitializedFilter(true);
    }
  }, [groupedBets, isLoading, hasInitializedFilter]);

  if (!isConnected) {
    return <Navigate to="/" replace />;
  }
  // Filter logic
  const filteredGroupedBets = groupedBets.map(group => {
    const filteredBets = group.bets.filter(bet => {
      const isWinning = isBetWinning(bet, group.video);
      const isResolved = group.video.isViewsSet;

      switch (filter) {
        case 'ready_to_claim':
          return isResolved && isWinning && !bet.claimed;
        case 'active':
          return !isResolved;
        case 'finished':
          return isResolved;
        case 'all':
        default:
          return true;
      }
    });

    return {
      ...group,
      bets: filteredBets
    };
  }).filter(group => group.bets.length > 0);

  // Calculate stats
  const totalWagered = groupedBets.reduce((sum, group) => sum + group.totalWagered, 0n);
  const activeBetsCount = groupedBets.reduce((count, group) => {
    return count + (group.video.isViewsSet ? 0 : group.bets.length);
  }, 0);
  const totalBetsCount = groupedBets.reduce((count, group) => count + group.bets.length, 0);

  const filters = [
    { id: 'ready_to_claim', label: 'Ready to Claim' },
    { id: 'active', label: 'Active' },
    { id: 'all', label: 'All Bets' },
    { id: 'finished', label: 'Finished' },
  ] as const;

  return (
    <div className="dapp-page">
      <DAppHeader />

      <main className="dapp-main">
        <div className="container">
          <div className="my-bets-content">
            <h1 className="dapp-title">My Bets</h1>
            <p className="dapp-subtitle">Track your predictions and claim rewards</p>

            {/* Stats Overview */}
            <div className="my-bets-stats">
              <div className="mb-stat-card">
                <div className="mb-stat-card__value">
                  {formatEther(totalWagered)} ETH
                </div>
                <div className="mb-stat-card__label">Total Wagered</div>
              </div>
              <div className="mb-stat-card">
                <div className="mb-stat-card__value">{activeBetsCount}</div>
                <div className="mb-stat-card__label">Active Bets</div>
              </div>
              <div className="mb-stat-card">
                <div className="mb-stat-card__value">{totalBetsCount}</div>
                <div className="mb-stat-card__label">Total Bets</div>
              </div>
            </div>

            {/* Filters */}
            <div className="bets-filters">
              {filters.map((f) => (
                <button
                  key={f.id}
                  className={`filter-btn ${filter === f.id ? 'active' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {isLoading ? (
              <div className="loading-state">
                <p>Loading your bets...</p>
              </div>
            ) : filteredGroupedBets.length > 0 ? (
              <div className="bets-list">
                {filteredGroupedBets.map((group) => (
                  <VideoGroupCard
                    key={Number(group.video.id)}
                    group={group}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No bets found for this filter.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
